/**
 * Reads the branding the server already injected into index.html before this
 * script ever ran (see app/backend/services/branding_injection.py, #2741
 * FAS2). Precedence across the whole app is:
 *
 *   server-injected branding  >  build-time customer/config.json import  >  hardcoded default
 *
 * The server injects a `<script id="hai-branding-meta" type="application/json">`
 * with whichever of `app_name` / `organization_name` were actually configured
 * (each independently optional), and — when CSS custom properties were part
 * of the branding — a `<style id="hai-branding-vars">` block that already
 * painted `:root`/`.dark` before this module even loaded. Both are absent
 * for a deployment with no branding record, which is what keeps that case
 * byte-identical to pre-#2741 behavior: every getter below falls through to
 * `undefined` and callers apply their existing config.json/default fallback
 * exactly as before.
 */

/** One uploaded branding asset, as injected by the server. */
export interface InjectedAsset {
  /** Content-addressed path, e.g. `/branding-assets/logo_small_light?v=<uuid>`. */
  url: string
  /** Intrinsic size, when the upload endpoint could determine it. */
  width?: number
  height?: number
}

export type InjectedAssetKey =
  | "logo_small_light"
  | "logo_small_dark"
  | "logo_large_light"
  | "logo_large_dark"
  | "logo_landing_light"
  | "logo_landing_dark"
  // Favicons are also emitted as <link rel="icon"> by the server; they appear
  // here so `use-favicon.ts` can tell a tenant favicon exists and stop
  // overwriting it with the build-time one.
  | "favicon_light"
  | "favicon_dark"
  // The avatar beside assistant messages (#3227); unset, the chat uses the small logo.
  | "assistant_avatar_light"
  | "assistant_avatar_dark"

export type InjectedBrandingLink = {
  id: string
  label: string
  url: string
  placement: "chat_footer"
}

/**
 * The voice window's ground as the tenant set it. Absent means the brand's
 * sky, which is also what every deployment without branding gets.
 */
export type InjectedVoiceStage =
  | { mode: "color"; color: string }
  | { mode: "image"; url: string; ink: "dark" | "light" }

/**
 * Per-deployment UI switches (#3225). Each one overrides a build-time
 * `customer/config.json` key; see `lib/ui-options.ts` for the mapping and the
 * fallback. Mirrors `core/branding/ui_options.py` on the server.
 */
export type UiOptionKey = "show_dev_options" | "show_thought_process" | "segment_demo_data"

/**
 * The color mode a user who has never picked one opens in (#3298). Mirrors
 * `core/branding/color_mode.py` on the server and the ThemeProvider's `Theme`.
 */
export type InjectedColorMode = "light" | "dark" | "system"

interface InjectedBrandingMeta {
  app_name?: string
  organization_name?: string
  assets?: Partial<Record<InjectedAssetKey, InjectedAsset>>
  links?: InjectedBrandingLink[]
  voice_stage?: unknown
  ui_options?: unknown
  enabled_languages?: unknown
  privacy_policy_url?: unknown
  default_color_mode?: unknown
}

const META_SCRIPT_ID = "hai-branding-meta"
const STYLE_ID = "hai-branding-vars"

let cache: InjectedBrandingMeta | null | undefined

function readInjectedBrandingMeta(): InjectedBrandingMeta | null {
  if (cache !== undefined) {
    return cache
  }
  if (typeof document === "undefined") {
    cache = null
    return cache
  }
  const el = document.getElementById(META_SCRIPT_ID)
  const raw = el?.textContent
  if (!raw) {
    cache = null
    return cache
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    cache =
      parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as InjectedBrandingMeta)
        : null
  } catch {
    cache = null
  }
  return cache
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined
}

/**
 * Strip angle brackets from an admin-editable identity string.
 *
 * These values are interpolated into i18n strings (`{{appName}}` with
 * `escapeValue: false`), and some locale strings are rendered through
 * html-react-parser — so a stored name containing markup would execute as
 * HTML on e.g. training pages. The admin API rejects `<`/`>` on write, but
 * a value stored before that check existed stays live until re-saved;
 * stripping at this single read boundary neutralizes it for every consumer.
 * No legitimate name contains angle brackets, and other characters (`&`,
 * quotes) stay untouched so plain-text renders of names like
 * "Johnson & Johnson" are unaffected.
 */
function stripMarkup(value: string | undefined): string | undefined {
  const stripped = value?.replace(/[<>]/g, "").trim()
  return stripped ? stripped : undefined
}

/** Server-injected app display name, or undefined if no branding record set one. */
export function getInjectedAppName(): string | undefined {
  return stripMarkup(nonEmptyString(readInjectedBrandingMeta()?.app_name))
}

/** Server-injected tenant/organization name, or undefined if no branding record set one. */
export function getInjectedOrganizationName(): string | undefined {
  return stripMarkup(nonEmptyString(readInjectedBrandingMeta()?.organization_name))
}

/** Chat-footer links the admin stored. Empty when branding has none. */
export function getInjectedLinks(): InjectedBrandingLink[] {
  const raw: unknown = readInjectedBrandingMeta()?.links
  if (!Array.isArray(raw)) {
    return []
  }
  const out: InjectedBrandingLink[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") {
      continue
    }
    const rec = entry as Record<string, unknown>
    const id = stripMarkup(nonEmptyString(rec.id))
    const label = stripMarkup(nonEmptyString(rec.label))
    const url = nonEmptyString(rec.url)
    if (!id || !label || !url || rec.placement !== "chat_footer") {
      continue
    }
    if (!/^https?:\/\//i.test(url)) {
      continue
    }
    out.push({ id, label, url, placement: "chat_footer" })
  }
  return out
}

/**
 * Whether the server injected CSS custom properties for `theme` into
 * `:root`/`.dark`. When true, code that applies build-time
 * `customer/config.json` colors at runtime (theme-provider.tsx) MUST skip
 * doing so for that theme — the server's values already painted before
 * first frame, and re-applying the build-time config would both be
 * redundant and, for a branded tenant whose colors differ from the generic
 * default config.json, would violate the injected > build-time precedence.
 */
export function hasInjectedBrandingStyles(theme: "light" | "dark"): boolean {
  if (typeof document === "undefined") {
    return false
  }
  const el = document.getElementById(STYLE_ID)
  if (!el) {
    return false
  }
  return el.getAttribute(theme === "light" ? "data-hai-light" : "data-hai-dark") === "1"
}

/**
 * A logo the customer's admin uploaded, or undefined if they haven't.
 *
 * The URL is content-addressed on the blob's own version, so a replacement
 * upload changes it and no cached copy can mask the new logo. `width`/`height`
 * are the uploaded image's intrinsic size — that is what a logo component
 * should derive its aspect ratio from, instead of the per-customer hardcoded
 * number that sits in `customer/small-logo.tsx` on every customer branch and
 * is the whole reason seven of those branches exist.
 */
export function getInjectedAsset(key: InjectedAssetKey): InjectedAsset | undefined {
  const asset = readInjectedBrandingMeta()?.assets?.[key]
  if (!asset || typeof asset !== "object") {
    return undefined
  }
  const url = nonEmptyString(asset.url)
  if (!url) {
    return undefined
  }
  const positive = (value: unknown): number | undefined =>
    typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined
  return { url, width: positive(asset.width), height: positive(asset.height) }
}

/** `aspect-ratio` for an injected asset, or undefined when the size is unknown. */
export function injectedAspectRatio(asset: InjectedAsset | undefined): string | undefined {
  return asset?.width && asset.height ? `${asset.width} / ${asset.height}` : undefined
}

/**
 * The CSS custom-property names the server injected for `theme`.
 *
 * `hasInjectedBrandingStyles` answers "did the server paint this theme at
 * all", which was too coarse: a branding record that sets three variables
 * caused the entire build-time palette to be skipped, so every variable the
 * record did NOT set fell back to the generic bundle CSS instead of the
 * customer's own config.json. Callers need to know which specific properties
 * are already handled.
 */
export function getInjectedCssVariableNames(theme: "light" | "dark"): Set<string> {
  const names = new Set<string>()
  if (typeof document === "undefined") {
    return names
  }
  const css = document.getElementById(STYLE_ID)?.textContent
  if (!css) {
    return names
  }
  // The server emits exactly `:root{...}` and `.dark{...}` — see
  // branding_injection._render_style_tag. Matching the selector rather than
  // parsing CSS properly is fine because we control both ends, and an
  // unrecognised shape degrades to "nothing injected", the safe direction.
  const selector = theme === "light" ? ":root" : "\\.dark"
  const block = new RegExp(`${selector}\\{([^}]*)\\}`).exec(css)
  if (!block) {
    return names
  }
  for (const declaration of block[1].split(";")) {
    const name = declaration.split(":")[0]?.trim()
    if (name.startsWith("--")) {
      names.add(name)
    }
  }
  return names
}

/** Test-only: clear the module-level memoized read so tests can re-seed the DOM. */
export function resetRuntimeBrandingCacheForTests(): void {
  cache = undefined
}

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i
// The one URL the server emits for this: the versioned same-origin asset
// route. Anything else is dropped, since it ends up inside a CSS url().
const VOICE_STAGE_IMAGE_URL_RE = /^\/branding-assets\/voice_stage_background\?v=[A-Za-z0-9._~-]+$/

/** The tenant's voice window ground, or undefined for the brand's sky. */
export function getInjectedVoiceStage(): InjectedVoiceStage | undefined {
  const raw: unknown = readInjectedBrandingMeta()?.voice_stage
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined
  }
  const rec = raw as Record<string, unknown>
  if (rec.mode === "color") {
    const color = nonEmptyString(rec.color)
    return color && HEX_COLOR_RE.test(color) ? { mode: "color", color } : undefined
  }
  if (rec.mode === "image") {
    const url = nonEmptyString(rec.url)
    if (!url || !VOICE_STAGE_IMAGE_URL_RE.test(url)) {
      return undefined
    }
    return { mode: "image", url, ink: rec.ink === "dark" ? "dark" : "light" }
  }
  return undefined
}

/**
 * A UI switch the admin set for this deployment, or undefined when it follows
 * the build. Only a real boolean counts: `"false"` is truthy, and a string
 * that slipped past the server would turn a switch on that was meant off.
 */
export function getInjectedUiOption(key: UiOptionKey): boolean | undefined {
  const raw: unknown = readInjectedBrandingMeta()?.ui_options
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined
  }
  const value = (raw as Record<string, unknown>)[key]
  return typeof value === "boolean" ? value : undefined
}

/**
 * The interface languages the admin limited this deployment to (#3226), in
 * the stored order, or undefined for "all". Only strings pass; which codes
 * the app actually supports is `lib/languages.ts`'s call.
 */
export function getInjectedEnabledLanguages(): string[] | undefined {
  const raw: unknown = readInjectedBrandingMeta()?.enabled_languages
  if (!Array.isArray(raw)) {
    return undefined
  }
  const codes = raw.filter((code): code is string => typeof code === "string")
  return codes.length > 0 ? codes : undefined
}

/**
 * The privacy policy the admin pointed this deployment at (#3243), or
 * undefined for the built-in default. https only, no credentials in the URL:
 * the same rule the server applies, re-checked because this ends up in an
 * href every new user is asked to follow.
 */
export function getInjectedPrivacyPolicyUrl(): string | undefined {
  const raw = nonEmptyString(readInjectedBrandingMeta()?.privacy_policy_url)?.trim()
  if (!raw) {
    return undefined
  }
  try {
    const url = new URL(raw)
    return url.protocol === "https:" && !url.username && !url.password ? raw : undefined
  } catch {
    return undefined
  }
}

const COLOR_MODES: readonly InjectedColorMode[] = ["light", "dark", "system"]

/**
 * The deployment's default color mode (#3298), or undefined when the admin
 * never set one (the caller then keeps its own default, light). Only the three
 * modes pass: anything else would leave the ThemeProvider with a theme it
 * cannot paint. It is a default only; a mode the user chose themselves is kept
 * in the browser and always wins.
 */
export function getInjectedDefaultColorMode(): InjectedColorMode | undefined {
  const raw: unknown = readInjectedBrandingMeta()?.default_color_mode
  return COLOR_MODES.find((mode) => mode === raw)
}
